import { LINK_TYPES, type Issue, type IssueLink, type WorkflowAction } from "@gjwtrack/domain";
import { fieldKey } from "@gjwtrack/query-language";
import { Copy, Ellipsis, Link as LinkIcon, Pencil, Play, Star, ThumbsUp, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { Centered } from "../App.tsx";
import { copyIdAndSummary, copyText, isCopyIssueShortcut } from "../lib/copyIssue.ts";
import { ActivityStream } from "../components/Activity.tsx";
import { Attachments } from "../components/Attachments.tsx";
import { Avatar, ValueBadge } from "../components/Badges.tsx";
import { FieldRow, PanelValue } from "../components/FieldPanel.tsx";
import { IssuesSidePanel } from "../components/IssuesSidePanel.tsx";
import { LinkDialog } from "../components/LinkDialog.tsx";
import { MarkdownEditor } from "../components/MarkdownEditor.tsx";
import { MarkdownView } from "../components/MarkdownView.tsx";
import { MergeRequests } from "../components/MergeRequests.tsx";
import { NewIssueDialog, type LinkTo } from "../components/NewIssueDialog.tsx";
import { TagPicker } from "../components/TagPicker.tsx";
import { TopBar } from "../components/TopBar.tsx";
import { Menu } from "../ui/Menu.tsx";
import { alertService, Button, Tag } from "../ui/ring.ts";
import { VisibilityMenu } from "../components/VisibilityMenu.tsx";
import { WorkItems } from "../components/WorkItems.tsx";
import { client, useClient } from "../lib/client.ts";
import { absTime, BUNDLE_KINDS, can, fieldValueText, issueKey, parseIssueKey, Permission, relTimeAgo, userName } from "../lib/format.ts";
import { getListContext } from "../lib/listContext.ts";
import { uuid } from "@gjwtrack/client";

export function IssuePage() {
  const { key = "" } = useParams();
  const parsed = parseIssueKey(key);
  const sub = `issue:${key}`;
  const navigate = useNavigate();

  useEffect(() => {
    if (!parsed) return;
    // A one-row live view: the issue's own updates (and comment / link bumps) arrive as `update`.
    client.openView(sub, `project: ${parsed.projectKey} number: ${parsed.number}`, 1);
    return () => client.closeView(sub);
  }, [sub, parsed?.projectKey, parsed?.number]);

  const view = useClient((s) => s.views[sub]);
  const id = view?.ids[0];
  const issue = useClient((s) => (id === undefined ? undefined : s.display[id]));
  const schema = useClient((s) => s.schema);
  const me = useClient((s) => s.me);
  // New issue opened from this page (YouTrack style: in place, optionally linked to this issue via the caret menu).
  const [creating, setCreating] = useState<{ linkTo?: LinkTo } | null>(null);
  const watchers = useClient((s) => (id === undefined ? undefined : s.watchers[id]));
  const [panel, setPanel] = useState(true); // YouTrack keeps the Issues side panel next to the issue
  const [actions, setActions] = useState<WorkflowAction[]>([]);
  useEffect(() => {
    if (id === undefined) return;
    let cancelled = false;
    client.http.issueActions(id).then((a) => !cancelled && setActions(a)).catch(() => !cancelled && setActions([]));
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (id === undefined) return;
    void client.loadComments(id);
    void client.loadLinks(id);
    void client.loadWatchers(id);
    return () => {
      client.forgetComments(id);
      client.forgetLinks(id);
    };
  }, [id]);
  // ⇧⌥⌘C copies "KEY summary" (YouTrack's shortcut for the breadcrumb copy icon).
  useEffect(() => {
    if (!issue || !schema) return;
    const onKey = (e: KeyboardEvent): void => {
      if (!isCopyIssueShortcut(e)) return;
      e.preventDefault();
      const key = issueKey(schema, issue);
      void copyIdAndSummary(key, issue.summary, `${location.origin}/issue/${key}`);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [issue, schema]);

  const [editingDesc, setEditingDesc] = useState(false);
  const [linking, setLinking] = useState(false);

  if (!parsed) return <Centered>Not an issue key.</Centered>;
  if (!view || !schema) return <Centered>Loading…</Centered>;
  if (view.status === "error") return <Centered>{view.error}</Centered>;
  if (!issue) return view.status === "live" && view.total === 0 ? <Centered>No such issue, or you cannot see it.</Centered> : <Centered>Loading…</Centered>;

  const editable = can(me, issue.projectId, Permission.UPDATE_ISSUE);
  const project = schema.projectsById.get(issue.projectId);
  const projectFields = schema.projectFieldsByProject.get(issue.projectId) ?? [];
  const k = issueKey(schema, issue);
  const listQuery = getListContext().query;

  return (
    <div className="flex h-full flex-col">
      <TopBar
        crumbs={[
          { label: "Projects", to: "/admin/projects" },
          { label: project?.name ?? String(issue.projectId), to: `/issues?q=${encodeURIComponent(`project: ${project?.key ?? ""}`)}` },
          { label: "Issues", to: listQuery ? `/issues?q=${encodeURIComponent(listQuery)}` : "/issues" },
          { label: k, strike: issue.resolvedAt !== null, copy: { key: k, summary: issue.summary, url: `${location.origin}/issue/${k}` }, mono: true },
        ]}
        back={listQuery ? `/issues?q=${encodeURIComponent(listQuery)}` : "/issues"}
        onTogglePanel={() => setPanel((p) => !p)}
        query={listQuery}
        onQuery={(q) => navigate(q ? `/issues?q=${encodeURIComponent(q)}` : "/issues")}
        currentIssueId={issue.id}
        onCreate={(opts) => setCreating(opts ?? {})}
      />
      <div className="flex min-h-0 flex-1">
        {panel && <IssuesSidePanel onQuery={(q) => navigate(q ? `/issues?q=${encodeURIComponent(q)}` : "/issues")} currentQuery={listQuery} onOpenDraft={(d) => navigate(`/issues?draft=${d}`)} />}
        {/* One block, YouTrack-style: the ticket and its field panel side by side, capped in width and centred, so a wide screen leaves room on both sides. */}
        <div className="flex min-h-0 min-w-0 flex-1 justify-center">
        <div className="flex min-h-0 w-full max-w-[86rem]">
        <div className="flex min-w-0 flex-1 flex-col" data-issue-id={issue.id} data-issue-key={k}>
         <div className="shrink-0 px-8 pt-4">
          {/* Created by / Updated by · visibility · like · star */}
          <div className="mb-3 flex items-center gap-4 text-[13.5px] whitespace-nowrap text-muted">
            <span>
              Created by <span className="text-value">{userName(schema, issue.reporterId)}</span> <span title={absTime(issue.createdAt)}>{relTimeAgo(issue.createdAt)}</span>
            </span>
            {issue.updatedBy !== null && issue.updatedAt !== issue.createdAt && (
              <span>
                Updated by <span className="text-value">{userName(schema, issue.updatedBy)}</span> <span title={absTime(issue.updatedAt)}>{relTimeAgo(issue.updatedAt)}</span>
              </span>
            )}
            <span className="flex-1" />
            <VisibilityMenu issueId={issue.id} visibility={issue.visibility} editable={editable} />
          </div>

          {/* Title row: star · title · icons */}
          <div className="mb-2 flex items-start gap-3">
            <button className={`icon-btn mt-1 shrink-0 ${watchers?.me ? "on" : ""}`} onClick={() => void client.watch(issue.id, !watchers?.me)} title="Star">
              <Star size={20} fill={watchers?.me ? "currentColor" : "none"} />
            </button>
            <InlineText value={issue.summary} editable={editable} className="min-w-0 flex-1 text-[26px] leading-tight font-semibold tracking-tight text-balance" onCommit={(summary) => client.updateIssue(issue.id, { summary })} />
            <div className="relative flex shrink-0 items-center gap-3 pt-1">
              <Button icon={Pencil} title="Edit description" onClick={() => setEditingDesc(true)} disabled={!editable} />
              <TagPicker issueId={issue.id} disabled={!editable} />
              <Button icon={LinkIcon} title="Link issue" onClick={() => setLinking(true)} disabled={!editable} />
              <Menu
                anchor={<Button icon={Ellipsis} title="More" />}
                items={[
                  { key: "link", label: "Copy link", icon: Copy, onClick: () => void copyText(`${location.origin}/issue/${k}`) },
                  { key: "id", label: "Copy issue ID", icon: Copy, onClick: () => void copyText(k) },
                  // Workflow buttons (onAction rules of the project's enabled workflows).
                  ...actions.map((a, i) => ({
                    key: `act:${a.workflowId}:${a.rule}`,
                    label: a.title,
                    icon: Play,
                    separator: i === 0,
                    onClick: () =>
                      void client.http
                        .command<{ ok: boolean; log: string; error: string | null }>("runAction", { workflowId: a.workflowId, rule: a.rule, issueId: issue.id }, uuid())
                        .then((r) => (r.ok ? alertService.successMessage(`${a.title}: done${r.log ? ` — ${r.log.split("\n").at(-1)}` : ""}`) : alertService.error(`${a.title}: ${r.error ?? "failed"}`)))
                        .catch((e) => client.pushError("action", (e as Error).message)),
                  })),
                ]}
              />
            </div>
          </div>
         </div>
         <div className="min-h-0 flex-1 overflow-auto px-8 pb-4">

          {issue.tags.length > 0 && (
            <div className="mb-4 ml-10 flex flex-wrap gap-1.5">
              {issue.tags.map((t) => (
                <Tag key={t} readOnly={!editable} {...(editable ? { onRemove: () => client.updateIssue(issue.id, { tags: issue.tags.filter((x) => x !== t) }) } : {})}>
                  {schema.tagsById.get(t)?.name ?? `#${t}`}
                </Tag>
              ))}
            </div>
          )}

          {/* Description */}
          <section className="mb-8 ml-10">
            {editingDesc ? (
              <MarkdownEditor
                initial={issue.descriptionText ?? ""}
                placeholder="Describe the issue…"
                autoFocus
                target={{ issueId: issue.id }}
                onSave={(md) => {
                  setEditingDesc(false);
                  if ((md || null) !== issue.descriptionText) client.updateIssue(issue.id, { description: md || null });
                }}
                onCancel={() => setEditingDesc(false)}
              />
            ) : issue.descriptionText ? (
              <div className={editable ? "cursor-text" : ""} onDoubleClick={() => editable && setEditingDesc(true)} title={editable ? "Double-click to edit" : undefined}>
                <MarkdownView text={issue.descriptionText} target={{ issueId: issue.id }} className="text-[15px]" />
              </div>
            ) : (
              <button className="text-faint hover:text-muted" onClick={() => editable && setEditingDesc(true)} disabled={!editable}>
                No description. {editable && "Click to add one."}
              </button>
            )}
          </section>

          <MergeRequests issueId={issue.id} updatedAt={issue.updatedAt} />
          <Attachments target={{ issueId: issue.id }} canAdd={can(me, issue.projectId, Permission.ADD_COMMENT)} />
          <WorkItems issueId={issue.id} updatedAt={issue.updatedAt} canLog={can(me, issue.projectId, Permission.UPDATE_ISSUE)} />
          <Links issueId={issue.id} editable={editable} />
          <InwardMentions issueId={issue.id} />
          <ActivityStream issueId={issue.id} canComment={can(me, issue.projectId, Permission.ADD_COMMENT)} />
         </div>
        </div>

        {/* Right panel: fields, YouTrack style (label / value / badge). No card: it sits flush against the content column. */}
        <aside className="w-72 shrink-0 overflow-y-auto px-5 pt-5 pb-4">
          <div className="mb-4 flex items-center gap-5">
            <button
              className={`flex items-center gap-1.5 text-[13.5px] ${me && issue.voters?.includes(me.id) ? "text-accent" : "text-muted hover:text-ink"}`}
              onClick={() => me && void client.vote(issue.id, !issue.voters?.includes(me.id))}
              title={me && issue.voters?.includes(me.id) ? "Take your vote back" : "Vote for this issue"}
            >
              <ThumbsUp size={16} fill={me && issue.voters?.includes(me.id) ? "currentColor" : "none"} />
              <span className="tabular-nums">{issue.voters?.length ?? 0}</span>
            </button>
            <button className={`flex items-center gap-1.5 text-[13.5px] ${watchers?.me ? "text-star" : "text-muted hover:text-ink"}`} onClick={() => void client.watch(issue.id, !watchers?.me)} title={watchers?.me ? "Stop watching" : "Watch this issue"}>
              <Star size={16} fill={watchers?.me ? "currentColor" : "none"} />
              <span className="tabular-nums">{watchers?.userIds.length ?? 0}</span>
            </button>
          </div>
          <FieldRow label="Project" badge={<span className="badge chip-1">{(project?.key ?? "?")[0]}</span>}>
            <Link to={`/issues?q=${encodeURIComponent(`project: ${project?.key ?? ""}`)}`} className="text-value hover:underline">
              {project?.name ?? issue.projectId}
            </Link>
          </FieldRow>
          {projectFields.map((pf) => {
            const def = schema.fieldsById.get(pf.fieldId);
            if (!def) return null;
            const fk = fieldKey(def.id);
            const values = issue.fields[fk] ?? [];
            const first = values[0];
            const badge =
              first === undefined ? null : BUNDLE_KINDS.has(def.kind) ? <ValueBadge schema={schema} def={def} value={first} /> : def.kind === "user" && typeof first === "number" ? <Avatar schema={schema} userId={first} /> : null;
            return (
              <FieldRow key={fk} label={pf.alias ?? def.name} badge={badge}>
                <PanelValue schema={schema} def={def} pf={pf} values={values} editable={editable} onChange={(v) => client.updateIssue(issue.id, { fields: { [fk]: v } })} />
              </FieldRow>
            );
          })}
          <FieldRow label="Reporter" badge={<Avatar schema={schema} userId={issue.reporterId} />}>
            <span className="text-value">{userName(schema, issue.reporterId)}</span>
          </FieldRow>
          <BoardRows issueId={issue.id} updatedAt={issue.updatedAt} />
        </aside>
        </div>
        </div>
      </div>
      {linking && <LinkDialog issueId={issue.id} onClose={() => setLinking(false)} />}
      {creating && (
        <NewIssueDialog
          {...(creating.linkTo ? { linkTo: creating.linkTo } : {})}
          onClose={() => setCreating(null)}
          onCreated={(created) => {
            setCreating(null);
            navigate(`/issue/${issueKey(schema, created)}`);
          }}
        />
      )}
    </div>
  );
}

/** Sprints and Boards rows, as YouTrack shows them; reloaded when the issue changes (a move touches nothing on the issue, so also on mount). */
function BoardRows({ issueId, updatedAt }: { issueId: number; updatedAt: number }) {
  const [rows, setRows] = useState<{ boardId: number; boardName: string; sprintId: number | null; sprintName: string | null }[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    client.http.issueBoards(issueId).then((r) => !cancelled && setRows(r)).catch(() => !cancelled && setRows([]));
    return () => {
      cancelled = true;
    };
  }, [issueId, updatedAt]);
  const sprints = (rows ?? []).filter((r) => r.sprintName);
  return (
    <>
      <FieldRow label="Sprints">
        {sprints.length ? sprints.map((r) => <Link key={r.boardId} to={`/agiles/${r.boardId}/${r.sprintId ?? "current"}`} className="mr-2 text-value hover:underline">{r.sprintName}</Link>) : <span className="text-value">Unscheduled</span>}
      </FieldRow>
      <FieldRow label="Boards">
        {rows === null ? <span className="text-faint">…</span> : rows.length ? rows.map((r) => (
          <Link key={r.boardId} to={`/agiles/${r.boardId}/${r.sprintId ?? "backlog"}`} className="chip mr-1 hover:underline">{r.boardName}{r.sprintName ? ` · ${r.sprintName}` : " · Backlog"}</Link>
        )) : <span className="text-faint">Not on a board</span>}
      </FieldRow>
    </>
  );
}

function InlineText({ value, editable, className, onCommit }: { value: string; editable: boolean; className: string; onCommit: (v: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);
  const finish = (commit: boolean): void => {
    setEditing(false);
    const v = draft.trim();
    if (commit && v && v !== value) onCommit(v);
  };
  if (!editing) {
    return (
      <h1 className={`${className} ${editable ? "cursor-text rounded hover:bg-surface" : ""}`} onClick={() => editable && setEditing(true)} title={editable ? "Click to edit" : undefined}>
        {value}
      </h1>
    );
  }
  return (
    <input
      className={`field w-full ${className}`}
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => finish(true)}
      onKeyDown={(e) => {
        if (e.key === "Enter") finish(true);
        if (e.key === "Escape") finish(false);
      }}
    />
  );
}

/** YouTrack's "Inward mentions": issues whose description or comments name this one. */
function InwardMentions({ issueId }: { issueId: number }) {
  const schema = useClient((s) => s.schema);
  const [items, setItems] = useState<Issue[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    setItems(null);
    client.http
      .mentions(issueId)
      .then((xs) => !cancelled && setItems(xs))
      .catch(() => !cancelled && setItems([]));
    return () => {
      cancelled = true;
    };
  }, [issueId]);
  if (!schema || !items || items.length === 0) return null;
  return (
    <section className="mb-8 ml-10">
      <div className="mb-1 text-[13px] text-muted">Inward mentions</div>
      <ul>
        {items.map((other) => {
          const ok = issueKey(schema, other);
          return (
            <li key={other.id} className="flex items-center gap-2 py-0.5">
              <Link to={`/issue/${ok}`} className={`font-mono text-[13px] ${other.resolvedAt !== null ? "text-faint line-through" : "text-value"}`}>
                {ok}
              </Link>
              <Link to={`/issue/${ok}`} className={`truncate hover:underline ${other.resolvedAt !== null ? "text-muted line-through" : ""}`}>
                {other.summary}
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function Links({ issueId, editable }: { issueId: number; editable: boolean }) {
  const links = useClient((s) => s.links[issueId]);
  const schema = useClient((s) => s.schema);
  const display = useClient((s) => s.display);
  const groups = useMemo(() => {
    const out = new Map<string, IssueLink[]>();
    for (const l of links?.items ?? []) {
      const def = LINK_TYPES[l.type];
      const label = l.direction === "out" ? def.outward : def.inward;
      const list = out.get(label);
      if (list) list.push(l);
      else out.set(label, [l]);
    }
    return [...out.entries()];
  }, [links]);
  if (!schema || groups.length === 0) return null;
  return (
    <section className="mb-8 ml-10">
      {groups.map(([label, items]) => (
        <div key={label} className="mb-3">
          <div className="mb-1 text-[13px] text-muted">{label[0]!.toUpperCase() + label.slice(1)}</div>
          <ul>
            {items.map((l) => {
              const other = display[l.issue.id] ?? l.issue;
              const ok = issueKey(schema, other);
              return (
                <li key={l.id} className="group flex items-center gap-2 py-0.5">
                  <Link to={`/issue/${ok}`} className={`font-mono text-[13px] ${other.resolvedAt !== null ? "text-faint line-through" : "text-value"}`}>
                    {ok}
                  </Link>
                  <Link to={`/issue/${ok}`} className={`truncate hover:underline ${other.resolvedAt !== null ? "text-muted line-through" : ""}`}>
                    {other.summary}
                  </Link>
                  {editable && (
                    <button className="icon-btn h-5 w-5 opacity-0 group-hover:opacity-100" title="Remove link" onClick={() => void client.unlinkIssues(issueId, l.id)}>
                      <X size={12} />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </section>
  );
}

